<template>
  <section class="section-container">
    <h2 v-if="isScenariosExists" class="scenarios-title">
      {{ $t('scenarios.title') }} <DocsLink link="toolguide/030-scenario.html" />
    </h2>
    <!-- :items-per-page="-1" is a workaround for Vuetify table to avoid pagination.
    Needs to be used together with disable-sort, disable-pagination and hide-default-footer -->
    <v-data-table
      class="scenarios-table"
      item-value="_id"
      :items-per-page="-1"
      :body-append="true"
      :items="editableScenarios"
      :loading="loading"
      :expanded="expandedScenario"
      hide-default-footer
      :header-props="{ style: 'display: none;' }"
    >
      <template #item="{ item, internalItem, isExpanded, index, toggleExpand }">
        <tr class="scenario">
          <td class="scenario__input-cell">
            <div class="scenario__input-container">
              <div class="scenario__input w-100">
                <v-form v-model="valid" @submit.prevent="">
                  <!-- TODO: Fix the validation rules not working -->
                  <AssortmentTextField
                    :key="item._id"
                    v-model="item.name"
                    :disabled="!canEditScenarioName"
                    :rules="rules"
                    bg-color="'white'"
                    style="padding-left: 7px"
                    variant="solo"
                    flat
                    hide-details
                    @blur="save(item)"
                    @keyup.enter="save(item)"
                    @keyup.esc="discard(item)"
                  />
                </v-form>
              </div>
            </div>
          </td>

          <td class="scenario__cell-container">
            <ActionableLink
              class="scenario__cell clickable"
              :on-click="() => toggleScenarioPanel({ item, internalItem, isExpanded })"
              :to="item.generatedRoutes['space']"
              right-click-only
            >
              {{ $t('scenarios.space') }}
              <ScenariosListIcon :item-status="item.status" :subsection="'space'" />
            </ActionableLink>
          </td>

          <td class="scenario__cell-container scenario__cell-container--main">
            <ActionableLink
              :on-click="() => toggleScenarioPanel({ item, internalItem, isExpanded })"
              class="scenario__cell clickable"
              :to="item.generatedRoutes['inputs']"
              right-click-only
            >
              {{ $t('scenarios.inputs') }}
              <ScenariosListIcon :item-status="item.status" :subsection="'inputs'" />
            </ActionableLink>
          </td>

          <td v-if="isFullResetWP" class="scenario__cell-container scenario__cell-container--sub">
            <ActionableLink
              class="scenario__cell clickable"
              :on-click="() => toggleScenarioPanel({ item, internalItem, isExpanded })"
              :to="item.generatedRoutes['measuring']"
              right-click-only
            >
              {{ $t('scenarios.measuring') }}
              <ScenariosListIcon :item-status="item.status" :subsection="'measuring'" />
            </ActionableLink>
          </td>

          <td v-if="isFullResetWP" class="scenario__cell-container scenario__cell-container--sub">
            <ActionableLink
              class="scenario__cell clickable"
              :on-click="() => toggleScenarioPanel({ item, internalItem, isExpanded })"
              :to="item.generatedRoutes['modelling']"
              right-click-only
            >
              {{ $t('scenarios.modelling') }}
              <ScenariosListIcon :item-status="item.status" :subsection="'modelling'" />
            </ActionableLink>
          </td>

          <td v-if="isFullResetWP" class="scenario__cell--container scenario__cell-container--main">
            <component
              :is="isAssortmentsPanelDisabled(item) ? 'div' : 'ActionableLink'"
              class="scenario__cell scenario__cell--assortments"
              :class="{
                clickable: !isAssortmentsPanelDisabled(item),
              }"
              :on-click="
                () =>
                  toggleScenarioPanel({
                    item,
                    internalItem,
                    isExpanded,
                    panel: 'assortment',
                  })
              "
              :to="item.generatedRoutes['assortments-panel']"
              right-click-only
            >
              {{ $t('scenarios.assortments') }}
              <ScenariosListIcon :item-status="item.status" :subsection="'assortment'" />
            </component>
          </td>

          <td v-if="isFullResetWP" class="scenario__cell-container scenario__cell-container--main">
            <ActionableLink
              class="scenario__cell scenario__cell--execution clickable"
              :to="item.generatedRoutes['execution']"
              :on-click="() => setSelectedScenario(item)"
            >
              {{ $t('scenarios.execution') }}
              <ScenariosListIcon :item-status="item.status" :subsection="'execution'" />
            </ActionableLink>
          </td>

          <td
            v-if="isSimpleSwapsWP"
            class="scenario__cell-container scenario__cell-container--main"
          >
            <ActionableLink
              class="scenario__cell clickable"
              :on-click="() => toggleExpand({ item, isExpanded: false, expand })"
              :to="item.generatedRoutes['swaps']"
              right-click-only
            >
              {{ $t('scenarios.swaps') }}
              <ScenariosListIcon :item-status="item.status" :subsection="'swaps'" />
            </ActionableLink>
          </td>

          <td
            v-if="enableFinalCheckpoints"
            class="scenario__cell-container scenario__cell-container--main text-center"
          >
            <v-icon size="24" primary>{{
              finalScenarioIds.has(item._id) ? 'mdi-star' : 'mdi-star-outline'
            }}</v-icon>
          </td>

          <td
            v-if="isSimpleSwapsWP"
            class="scenario__cell-container scenario__cell-container--main"
          >
            <ActionableLink
              class="scenario__cell clickable"
              :on-click="() => toggleExpand({ item, isExpanded: false, expand })"
              :to="item.generatedRoutes['extracts']"
              right-click-only
            >
              {{ $t('scenarios.extracts') }}
              <ScenariosListIcon :item-status="item.status" :subsection="'extracts'" />
            </ActionableLink>
          </td>

          <td class="scenario__cell-container scenario__cell-container--main">
            <div class="scenario__cell scenario__cell--last-edit">
              <span>
                {{ $t('scenarios.scenarioLastEdit') }}
                <b>{{ formatDate(item.lastModifiedDate, getDateFormats.longWithTime) }}</b>
              </span>
              <Notes
                :notes="getNotes(item._id)"
                :loading="loadingNotes"
                :title="$t('notes.types.scenario')"
                :max-chars="getClientConfig.notesSettings.maxCharSize"
                :notifications="!!item.totalNotes"
                @post="postScenarioNote(item._id, index, $event)"
                @menu-open="fetchScenarioNotes(item._id)"
                @delete="deleteScenarioNote(item._id, index, $event)"
                @update="updateScenarioNote(item._id, index, $event)"
              />
            </div>
          </td>

          <td class="expand-button-container">
            <div class="scenario__cell scenario__cell--icons">
              <!-- Option buttons -->
              <ScenarioOptions :scenario="item" />
              <MainExpandButton
                :class="{ 'float-right': 'float-right' }"
                :is-expanded="isExpanded(internalItem)"
                @expand="toggleScenarioPanel({ item, internalItem, isExpanded })"
              />
            </div>
          </td>
        </tr>

        <tr class="scenario spacer">
          <td class="spacer-line" colspan="12" />
        </tr>
      </template>

      <template #expanded-row="{ item }">
        <tr>
          <td class="scenario__mgmt-panel-container" colspan="12">
            <component :is="selectedPanel" :scenario="item" />
          </td>
        </tr>
        <tr class="scenario spacer">
          <td class="spacer-line" colspan="12" />
        </tr>
      </template>

      <template #tfoot>
        <tr>
          <td colspan="100%">
            <ScenariosCreateForm
              :workpackage-id="selectedWorkpackage._id"
              @save:scenario="fetchWpScenarios"
            />
          </td>
        </tr>
      </template>

      <template #no-data>
        <div class="no-data-text">
          <span>{{ $t('scenarios.noScenariosToShow') }}</span>
          <DocsLink link="toolguide/030-scenario.html" />
        </div>
      </template>

      <template #bottom />
    </v-data-table>
  </section>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { filter, cloneDeep, get, isFunction } from 'lodash';

import sectionStatuses from '@enums/section-status';
import scenarioErrors from '@enums/scenario-errors';
import panels from '@enums/scenario-panels';
import contextEnum from '@enums/note-contexts';
import toolbarTabs from '@/utils/tabs/toolbar-tabs-enum';
import { useDayjs } from '@/composables/use-dayjs';
import { usePermission } from '@/composables/use-permission';
import { useInputValidations } from '@/composables/input-validations';
import { useContextStore } from '@/stores/context';
import { useFeature } from '@/composables/use-feature';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useNotesStore } from '@/stores/notes';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useScenariosStore } from '@/stores/scenarios';

export default {
  setup() {
    const { canEditScenarioName } = usePermission();
    const { formatDate } = useDayjs();
    const { enableFinalCheckpoints } = useFeature();

    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();
    return {
      canEditScenarioName,
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
      formatDate,
      enableFinalCheckpoints,
    };
  },

  data() {
    return {
      rules: [this.required, this.isUniqueScenarioName],
      loadingNotes: false,
      scenarioNameErrors: {},
      scenarioNameServerError: '',
      selectedPanel: panels.management,
      valid: true,
      sectionStatuses,
      expandedScenario: [],
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, ['finalScenarioIds']),
    ...mapState(useScenariosStore, ['selectedScenario', 'loading', 'scenarios', 'scenarioStatus']),
    ...mapState(useWorkpackagesStore, ['selectedWorkpackage', 'isFullResetWP', 'isSimpleSwapsWP']),
    ...mapState(useContextStore, ['getDateFormats', 'getClientConfig']),
    ...mapState(useNotesStore, ['getScenarioNotes']),

    isScenariosExists() {
      return this.scenarios.length;
    },

    editableScenarios() {
      // Create a copy of the scenarios for editing
      // Generate the routes for the scenario + attach
      // Add note functions to scenario
      return cloneDeep(this.scenarios).map(scenario => {
        const scenarioId = scenario._id;
        const currentScenarioStatus = this.scenarioStatus(scenario);
        scenario.generatedRoutes = toolbarTabs.ALL.reduce((result, tab) => {
          let tabRoute;
          if (isFunction(tab.firstChild)) {
            tabRoute = tab.firstChild(currentScenarioStatus, this.getClientConfig);
          } else {
            tabRoute = tab.firstChild || tab.routeName;
          }
          return {
            ...result,
            [tab.routeName]: {
              name: tabRoute,
              params: { scenarioId },
            },
          };
        }, {});
        scenario.isLoading = false;

        return scenario;
      });
    },

    scenarioIds() {
      return this.scenarios.map(scenario => scenario._id);
    },
  },

  async created() {
    await Promise.all([this.fetchFinalScenarios(), this.fetchWpScenarios()]);

    // If this wp contains the selected scenario, set that scenario to be open on load
    if (this.selectedScenario && this.scenarioIds.includes(this.selectedScenario._id)) {
      this.expandedScenario = [this.selectedScenario._id];
    } else if (this.scenarios.length === 1) {
      this.expandedScenario = [this.scenarios[0]._id];
      this.setSelectedScenario(this.scenarios[0]);
    }
  },

  methods: {
    ...mapActions(useScenariosStore, ['fetchScenarios', 'updateScenario', 'setSelectedScenario']),
    ...mapActions(useNotesStore, ['fetchNotes', 'postNote', 'updateNote', 'deleteNote']),
    ...mapActions(useAssortmentCanvasStore, ['fetchFinalScenarios']),

    getNotes(scenarioId) {
      return this.getScenarioNotes({ scenarioId });
    },

    async postScenarioNote(id, index, { message }) {
      this.loadingNotes = true;
      await this.postNote({
        params: {
          message,
          id,
          context: contextEnum.scenario,
          workpackageId: this.selectedWorkpackage._id,
        },
      });
      this.loadingNotes = false;
    },

    async fetchScenarioNotes(id) {
      this.loadingNotes = true;
      await this.fetchNotes({
        params: {
          id,
          context: contextEnum.scenario,
        },
      });
      this.loadingNotes = false;
    },

    async deleteScenarioNote(id, index, { noteId }) {
      this.loadingNotes = true;
      await this.deleteNote({
        params: {
          id,
          context: contextEnum.scenario,
          noteId,
        },
      });
      this.loadingNotes = false;
    },

    async updateScenarioNote(id, index, { message, note }) {
      this.loadingNotes = true;
      await this.updateNote({
        params: {
          id,
          context: contextEnum.scenario,
          message,
          noteId: note._id,
        },
      });
      this.loadingNotes = false;
    },

    isUniqueScenarioName(value) {
      if (this.editableScenarios.filter(sc => sc.name === value.trim()).length > 1) {
        return this.$t('validationErrors.unique', [this.$t('scenarios.scenarioName')]);
      }
      return true;
    },

    isAssortmentsPanelDisabled(item) {
      const isProductModellingComplete =
        get(item, 'status.modelling.productModelling.status') === sectionStatuses.complete;
      const noCanvas = get(item, 'status.assortment.status') === sectionStatuses.notStarted;
      return (
        get(item, 'status.measuring.cdt.status', sectionStatuses.failed) !==
          sectionStatuses.complete ||
        (!isProductModellingComplete && noCanvas)
      );
    },

    selectPanel(panel) {
      this.selectedPanel = panels[panel] || panels.management;
    },

    async fetchWpScenarios() {
      const workpackageId = this.selectedWorkpackage._id;
      await this.fetchScenarios({ params: { where: { workpackageId } } });
    },

    async save(item) {
      if (!this.valid) return false;
      const result = await this.updateScenario({
        scenario: {
          id: item._id,
          name: item.name,
        },
      });
      // This condition exists for the cases where the name was not repeated in the browser,
      // but when it was getting saved in the db, the same name had actually been used up, so an error got returned.
      // This condition allows server side validation errors to be displayed underneath the text box, just like the client side validations display errors.
      if (filter(result.errors, e => e.code === scenarioErrors.Errors.ScenarioNotUnique).length) {
        this.scenarioNameServerError = this.$t('validationErrors.unique', [
          this.$t('scenarios.scenarioName'),
        ]);
      }

      await this.fetchWpScenarios();
    },

    toggleScenarioPanel({ item: scenario, internalItem, panel, isDisabled = false, isExpanded }) {
      if (isDisabled) return;

      const targetPanel = panels[panel] || panels.management;
      const expandingPanel = !isExpanded(internalItem) || this.selectedPanel !== targetPanel;
      if (expandingPanel) {
        this.expandedScenario = [scenario._id];
        this.setSelectedScenario(scenario);
        this.selectPanel(panel);
      } else {
        this.expandedScenario = [];
        this.setSelectedScenario(null);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.section-container {
  position: relative;
  width: 100%;

  .scenarios-title {
    font-weight: bold;
    position: absolute;
    top: -30px;
    left: 2px;
    display: block;
    font-size: 1.2rem;
    margin-bottom: 15px;
  }

  .scenario__cell {
    font-size: 1.25rem !important;
    text-decoration: none;
    display: flex;
    justify-content: space-evenly;
  }
  .scenarios-table.v-data-table {
    background: $assortment-background;

    .v-data-table-rows-no-data {
      font-size: 1.2rem;
      width: 100%;
      height: 100%;
      white-space: normal;
    }

    table {
      border-collapse: separate; /* Don't collapse */
      tbody {
        .scenario {
          height: 41px;

          &.spacer {
            height: 5px;

            .spacer-line {
              height: 7px;
            }
            td {
              background-color: $assortment-background;
              height: 10px;
            }
          }
          td {
            padding-left: 10px;
            .col {
              padding: 0px;
              padding-left: 0px;
            }
            margin: 0px;
            padding: 0px;
            background-color: $assortment-scenarios-colour;
            height: 2.2rem;
            font-size: 1.2rem;
            &:not(.col-no-border) {
              //delete default border
              border-bottom-style: none;
            }
          }
          .row {
            height: 28px;
            .col {
              display: flex;
              align-items: center;
            }
          }

          .expand-button-container {
            padding-left: 5px;
          }

          td {
            position: relative;
          }

          &__input-container {
            width: 100%;
            display: flex;
            padding: 0 6px;
          }

          &__input-cell {
            min-width: 300px;
            background-color: $assortment-scenarios-colour;
          }

          &__cell-container {
            a {
              text-decoration: none;
              color: inherit;
            }

            &--main {
              &::after {
                content: '';
                background: $assortment-navbar-main-divider-colour;
                position: absolute;
                bottom: 15%;
                left: 0;
                height: 70%;
                width: 2px;
              }
            }

            &--sub {
              &::after {
                content: '';
                background: $assortment-navbar-sub-divider-colour;
                position: absolute;
                bottom: 25%;
                left: 0;
                height: 50%;
                width: 1px;
              }
            }
          }

          &__cell {
            display: flex;
            justify-content: space-evenly;
            align-items: center;
            padding: 0px 6px 0px 10px;
            background-color: $assortment-scenarios-colour;
            text-decoration: none;
            color: inherit;

            &--assortments,
            &--execution {
              padding: 0px 3px 0px 10px;
            }

            &--last-edit {
              padding: 0 10px;
              &::after {
                content: '';
                background: $assortment-navbar-sub-divider-colour;
                position: absolute;
                bottom: 20%;
                right: 0;
                height: 60%;
                width: 0.1rem;
              }
            }

            &--icons {
              justify-content: flex-end;
              padding-right: 3px;

              .v-icon:not(:last-child) {
                margin-right: 7px;
                color: $assortment-primary-colour;
              }
            }
          }

          :deep(.error-triangle-container) {
            padding-left: 5px;
          }

          &__mgmt-panel-container {
            padding: 0;
            border-bottom: none;
          }
        }
      }
    }
  }
  .scenarios-create-form-wrapper {
    display: table-row-group;
  }
}
</style>
